import {
  createElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentType,
  type ReactNode,
  type RefObject,
} from "react";
import type { AnimatedNumberProps } from "./animated-number";
import { useLiveChange } from "./live-change";
import { MOTION } from "./motion";
import type { SwapProps } from "./swap";

/*
 * What changes in place and rides in every page's start-up (spec 07 ›
 * Movimiento): Button's busy crossfade and arming rule (button.tsx keeps the
 * component), the lazy Swap of IconButton and StatusPill and the lazy
 * AnimatedNumber of CountBadge. One module, so the boot carries one chunk for
 * them.
 */

/** What a primary does now (`armKey`): a primitive, compared with `Object.is`. */
export type ArmKey = string | number | boolean | null;

/** jsdom and the server run no transitions: there a busy button changes at once. */
const FADES = typeof Element !== "undefined" && typeof Element.prototype.getAnimations === "function";

/**
 * Loading, on a change after the mount (spec 07 › Movimiento): the label and
 * the spinner cross in place on transitions of opacity (`[data-busy]` in
 * globals.css): the part that leaves in 80 on --ease-move (the eye is on
 * it), the one that comes in 120. Caught halfway, a transition turns back
 * from where it is, so a spinner that barely showed never flashes to full.
 * The spinner stays until it has faded (`onSpinnerEnd`). The label keeps
 * its wrapper from its first wait on (`wrapped`, `display: contents` at
 * rest): unwrapping it would remount the label once it shows again, and cut
 * a Swap that enters in it («Guardar» → «Guardado»). With reduced motion,
 * the same fades in 120.
 */
export function useBusyFade(loading: boolean) {
  const live = useLiveChange(loading);
  const [shown, setShown] = useState(loading);
  const [wrapped, setWrapped] = useState(loading);
  if (loading && !shown) setShown(true);
  else if (!loading && shown && !FADES) setShown(false);
  if (loading && !wrapped) setWrapped(true);
  const label = useRef<HTMLSpanElement>(null);
  const spinner = useRef<HTMLSpanElement>(null);
  const was = useRef(shown);
  useLayoutEffect(() => {
    const there = was.current;
    was.current = shown;
    if (!live || !FADES || !shown) return;
    // Both parts start with this change (the spinner mounts, the label's
    // wrapper mounts or leaves `contents`): each starts from where the other
    // state has it. One style flush for the two: a flush for the first would
    // give the second its first style at rest, and it would never move.
    if (!there) {
      const parts = [label.current, spinner.current];
      parts.forEach((el, i) => el?.style.setProperty("opacity", i ? "0" : "1"));
      parts.forEach((el) => el && getComputedStyle(el).opacity);
      parts.forEach((el) => el?.style.removeProperty("opacity"));
    }
    // Done: nothing to wait for if the spinner's fade never ran (it was already out, or hidden).
    if (!loading && !spinner.current?.getAnimations().length) void Promise.resolve().then(() => setShown(false));
  }, [loading, live, shown]);
  const onSpinnerEnd = (event: { target: EventTarget; currentTarget: EventTarget }) => {
    if (!loading && event.target === event.currentTarget) setShown(false);
  };
  return { shown, wrapped, label, spinner, onSpinnerEnd };
}

/**
 * The arming rule (spec 07 › Movimiento, `armKey`): when the key changes
 * after the mount, for `MOTION.arm.ms` (400) the button ignores its
 * activation, and so does a press that was down when it changed or began in
 * that time, until it ends: two taps 120 ms apart are one activation, and
 * the second never confirms what the first asked. An Enter inside the time,
 * or a held one (`repeat`) ever, does nothing. The label changes at t0 and
 * nothing else shows: `data-arming` while it lasts, never a grey, a cursor
 * or `aria-disabled`. Reduced motion and hot mode leave it as it is.
 */
export function useArming(button: RefObject<HTMLButtonElement | null>, armKey: ArmKey | undefined) {
  const live = useLiveChange(armKey);
  const armable = armKey !== undefined;
  useEffect(() => {
    const el = button.current;
    if (!armable || !el) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && event.repeat) event.preventDefault();
    };
    el.addEventListener("keydown", onKeyDown);
    return () => el.removeEventListener("keydown", onKeyDown);
  }, [button, armable]);
  useLayoutEffect(() => {
    const el = button.current;
    if (!live || !el) return;
    let open = true;
    let press = false;
    try {
      press = el.matches(":active");
    } catch {
      // An engine without :active: only the time arms it.
    }
    const mark = () => el.toggleAttribute("data-arming", open || press);
    const onPointerDown = () => {
      press = open;
      mark();
    };
    const onPointerCancel = () => {
      press = false;
      mark();
    };
    const onClick = (event: MouseEvent) => {
      // A click from the keyboard or a script (detail 0) has no press: only the time counts.
      const pointer = event.detail !== 0;
      if (open || (pointer && press)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      if (pointer) press = false;
      mark();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (open && event.key === "Enter") event.preventDefault();
    };
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointercancel", onPointerCancel);
    el.addEventListener("click", onClick, true);
    el.addEventListener("keydown", onKeyDown);
    const timer = window.setTimeout(() => {
      open = false;
      mark();
    }, MOTION.arm.ms);
    mark();
    return () => {
      window.clearTimeout(timer);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointercancel", onPointerCancel);
      el.removeEventListener("click", onClick, true);
      el.removeEventListener("keydown", onKeyDown);
      el.removeAttribute("data-arming");
    };
  }, [button, live, armKey]);
}

/*
 * What changes in place for the primitives in the start-up of every page
 * (IconButton in the shell and the login, StatusPill in the landing's and the
 * login's mocks, CountBadge in the tab bar and the sidebar): a glyph, a status
 * or a count only changes after the mount, so Swap and AnimatedNumber load
 * after the first commit, outside the boot. Until one lands `fallback`
 * renders (nothing can change yet; a change that comes before it lands at
 * once), and the server snapshot keeps hydration equal.
 */
function afterMount<P extends object>(load: () => Promise<ComponentType<P>>) {
  let loaded: ComponentType<P> | undefined;
  let loading: Promise<void> | undefined;
  const waiting = new Set<() => void>();
  const subscribe = (onLoad: () => void) => {
    waiting.add(onLoad);
    return () => {
      waiting.delete(onLoad);
    };
  };
  /** The component once its module has loaded (it starts loading at the mount); `fallback` until then. */
  return function AfterMount({ fallback, ...props }: P & { fallback: ReactNode }) {
    const component = useSyncExternalStore(
      subscribe,
      () => loaded,
      () => undefined,
    );
    useEffect(() => {
      if (loaded) return;
      void (loading ??= load().then(
        (module) => {
          loaded = module;
          waiting.forEach((onLoad) => onLoad());
        },
        // A chunk that failed to load (offline, a new deploy): the fallback stays, the next mount tries again.
        () => {
          loading = undefined;
        },
      ));
    }, []);
    return component ? createElement(component, props as P) : fallback;
  };
}

export const LazySwap = /* @__PURE__ */ afterMount<SwapProps>(() => import("./swap").then((module) => module.Swap));
// Next to LazySwap: its preload list already names what AnimatedNumber needs (React, motion.ts), so the shell pays only for its own chunk.
export const LazyAnimatedNumber = /* @__PURE__ */ afterMount<AnimatedNumberProps>(() =>
  import("./animated-number").then((module) => module.AnimatedNumber),
);
